/*
  SHOE-INN product page scripts (sections/main-product-v2.liquid), 2026-10-14.
  Moved here from two inline <script> blocks at the bottom of that section (about 35 KB of HTML on every product
  page, re-sent on every visit) so the browser caches them once. Loaded with `defer` from the section, which
  queues it behind global.js in document order: every custom element (variant-radios, media-gallery, the
  sticky bar) exists by the time this runs, and the DOMContentLoaded / load listeners below still fire.
  The only Liquid the inline version used was the product form id; the section now prints it as
  data-si-product-form on its root element (.product-main-template-v2).
  The one piece that stayed inline is the six-line "open the Reviews row instead of the default rows" bootstrap,
  because it has to run before first paint (an accordion must not open and then snap shut).
  Nothing below changed in behaviour. Part 1 is the colour / gallery / wishlist code, part 2 the 2026-10-14 redesign.
  Part 3 (new) is a small safety net for the lazily loaded "You may also like" rails.
*/

// ---------- Part 1: colour swatches, gallery and wishlist ----------
// ============================================
// 1. COLOR SWATCH & IMAGE ACTIVATION
// ============================================
document.addEventListener('DOMContentLoaded', function() {
  
  // Function to activate images by color title
  // 2026-10-14 (product page revamp): colour gating now uses its own class, is-colour-match, on both the
  // gallery slides and the thumbnail rail. is-active is left to the theme (media-gallery.js / global.js),
  // where it means "the one slide on screen", so the thumbnail-rail layout shows a single large photo.
  function showAllMedia() {
    document.querySelectorAll('.product__media-item, .thumbnail-list__item').forEach(function(item) {
      item.classList.add('is-colour-match');
    });
  }

  function ensureActiveMediaMatches() {
    const slides = Array.from(document.querySelectorAll('.product__media-item'));
    const active = slides.find(function(slide) { return slide.classList.contains('is-active'); });
    if (active && active.classList.contains('is-colour-match')) return;
    const first = slides.find(function(slide) { return slide.classList.contains('is-colour-match'); });
    if (!first) return;
    const gallery = document.querySelector('media-gallery');
    if (gallery && typeof gallery.setActiveMedia === 'function' && first.dataset.mediaId) {
      try { gallery.setActiveMedia(first.dataset.mediaId, true, false); return; } catch (e) { /* fall through */ }
    }
    slides.forEach(function(slide) { slide.classList.remove('is-active'); });
    first.classList.add('is-active');
  }

  // Sweep 2026-10-14 (H1): the colour whose photos are on show. On phones the gallery is a horizontal slider;
  // a swipe moves its scrollLeft, and a colour tap re-filters the slides but left that scroll where it was, so
  // the shopper landed on the new colour's 2nd photo with the counter on "2 of N". The slider goes back to its
  // first photo only when the colour actually changes (a size tap also re-runs this with the same colour, and
  // must not throw away a swipe).
  let shownColourKey = null;

  function activateImagesByTitle(title) {
    if (!title) {
      return;
    }
    const key = title.toLowerCase();
    const items = document.querySelectorAll('.product__media-item, .thumbnail-list__item');
    const matches = document.querySelectorAll(`.product__media-item[data-target-id="${key}"]`);
    let movedUntagged = false;

    if (matches.length > 0) {
      items.forEach(function(item) {
        const id = item.dataset.targetId || '';
        item.classList.toggle('is-colour-match', id === '' || id === key);
      });
      // Sweep 2026-10-14 (H4): photos with no alt text show under every colour, and Shopify lists them wherever
      // Katie uploaded them (Sadie's first photo is one). The colour's own tagged photos lead; the untagged ones
      // follow them. Only moved when one actually sits ahead of a tagged photo, so a re-run for the same colour
      // (a size tap) touches nothing. global.js still brings the chosen size's own photo to the front afterwards.
      ['.product__media-list', '.thumbnail-list'].forEach(function (listSelector) {
        const list = document.querySelector('.product-main-template-v2 ' + listSelector);
        if (!list) return;
        const tagged = Array.from(list.children).filter(function (li) { return (li.dataset.targetId || '') === key; });
        const blank = Array.from(list.children).filter(function (li) { return li.matches('[data-target-id=""]'); });
        if (!tagged.length || !blank.length) return;
        const lastTagged = tagged[tagged.length - 1];
        const needsMove = blank.some(function (li) { return li.compareDocumentPosition(lastTagged) & Node.DOCUMENT_POSITION_FOLLOWING; });
        if (!needsMove) return;
        blank.forEach(function (li) { list.appendChild(li); });
        movedUntagged = true;
        // The moved photo may have been the active slide; the colour's first photo takes over (and the slider
        // goes back to it below), or the browser's scroll anchoring would follow the moved photo to the end.
        if (listSelector === '.product__media-list') {
          list.querySelectorAll('.is-active').forEach(function (li) { li.classList.remove('is-active'); });
          tagged[0].classList.add('is-active');
        }
      });
    } else {
      // No photo carries this colour in its alt text: show everything rather than nothing.
      showAllMedia();
    }
    ensureActiveMediaMatches();
    if (key !== shownColourKey || movedUntagged) {
      const colourChanged = shownColourKey !== null;
      shownColourKey = key;
      if (colourChanged || movedUntagged) {
        const slider = document.querySelector('.product-main-template-v2 .product__media-list');
        if (slider && slider.scrollLeft !== 0) {
          try { slider.scrollTo({ left: 0, behavior: 'auto' }); } catch (e) { slider.scrollLeft = 0; }
        }
      }
    }
    const viewer = document.querySelector('product-gallery');
    if (viewer && typeof viewer.resetPages === 'function' && viewer.slider) viewer.resetPages();
    const rail = document.querySelector('thumbnail-slider');
    if (rail && typeof rail.resetPages === 'function' && rail.slider) rail.resetPages();
  }
  
  // Function to get color from checked radio
  function getSelectedColor() {
    const checkedColorRadio = document.querySelector('input[name="Color"]:checked');
    if (checkedColorRadio) {
      return checkedColorRadio.value;
    }
    return null;
  }
  
  // Function to activate swatch and images based on color value
  function activateColorByValue(colorValue) {

    // Find the radio input with this value
    const colorRadio = document.querySelector(`input[name="Color"][value="${colorValue}"]`);
    if (!colorRadio) {
      return;
    }
    
    // Check the radio
    colorRadio.checked = true;
    
    // Find the swatch label
    const colorSwatch = document.querySelector(`label[for="${colorRadio.id}"]`);
    if (colorSwatch && colorSwatch.classList.contains('color__swatch')) {
      // Remove active from all swatches
      document.querySelectorAll('.color__swatch').forEach(function(s) {
        s.classList.remove('active');
      });
      
      // Add active to this swatch
      colorSwatch.classList.add('active');
      
      // Get title and activate images
      const title = colorSwatch.getAttribute('data-title');
      if (title) {
        activateImagesByTitle(title);
      }
    }
  }
  
  // Add click event handlers to color swatches
  const colorSwatches = document.querySelectorAll('.color__swatch');
  
  if (colorSwatches.length > 0) {
    colorSwatches.forEach(function(swatch) {
      swatch.addEventListener('click', function(e) {
        
        const title = this.getAttribute('data-title');
        const inputId = this.getAttribute('for');
        const input = document.getElementById(inputId);
        
        if (input && !input.disabled) {
          // Check the radio
          input.checked = true;
          
          // Remove active from all swatches
          document.querySelectorAll('.color__swatch').forEach(function(s) {
            s.classList.remove('active');
          });
          
          // Add active to clicked swatch
          this.classList.add('active');
          
          // Activate images
          if (title) {
            activateImagesByTitle(title);
          }
          
          // Trigger change event on radio for variant system
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
  }
  
  // Listen for variant change events from VariantSelects
  document.addEventListener('variant:change', function(evt) {
    const variant = evt.detail.variant;
    
    if (variant && variant.option2) {
      // option2 is typically the color
      activateColorByValue(variant.option2);
    }
  });
  
  // Initialize: Check URL for variant, then auto-select or use current
  function initializeVariantAndImages() {

    // Products without a Color option (e.g. the gift card): the gallery is keyed to colors, so show every image.
    if (!document.querySelector('input[name="Color"]')) {
      showAllMedia();
      return;
    }

    // Check if there's a variant in the URL
    const urlParams = new URLSearchParams(window.location.search);
    const variantIdFromUrl = urlParams.get('variant');
    
    if (variantIdFromUrl) {
      // Wait for VariantSelects to initialize from URL
      // Check multiple times for the radio to be selected
      let attempts = 0;
      const checkInterval = setInterval(function() {
        attempts++;        
        const selectedColor = getSelectedColor();
        if (selectedColor) {
          clearInterval(checkInterval);
          activateColorByValue(selectedColor);
        } else if (attempts >= 20) {
          clearInterval(checkInterval);
          // Fallback: show all images
          showAllMedia();
        }
      }, 100);
      
    } else {
      // No URL variant - auto-select first available
      
      setTimeout(function() {
        // Check if already selected
        const alreadySelected = getSelectedColor();
        if (alreadySelected) {
          activateColorByValue(alreadySelected);
          return;
        }
        
        // Size is deliberately left unselected (no preselected size; see needs_size_choice), so only a
        // color is auto-selected here when none is checked.

        // Auto-select first available color
        const colorInputs = document.querySelectorAll('input[name="Color"]');
        for (let i = 0; i < colorInputs.length; i++) {
          if (!colorInputs[i].disabled) {
            const colorValue = colorInputs[i].value;
            const colorLabel = colorInputs[i].nextElementSibling;
            if (colorLabel) {
              colorLabel.click();              
              // Give it time to process, then activate
              setTimeout(function() {
                activateColorByValue(colorValue);
              }, 100);
            }
            break;
          }
        }
      }, 300);
    }
  }
  
  // Start initialization after a delay to let VariantSelects initialize
  setTimeout(function() {
    initializeVariantAndImages();
  }, 200);
  
  // Handle .cstmblack click
  const cstmBlackElements = document.querySelectorAll('.cstmblack');
  if (cstmBlackElements.length > 0) {
    cstmBlackElements.forEach(function(element) {
      element.addEventListener('click', function() {
        const activeSwatch = document.querySelector('.color__swatch.active');
        if (activeSwatch) {
          const title = activeSwatch.getAttribute('data-title');
          activateImagesByTitle(title);
        }
      });
    });
  }
});

// ============================================
// 2. MOBILE RESPONSIVE MEDIA LIST
// ============================================
document.addEventListener('DOMContentLoaded', function () {
  const mediaLists = document.querySelectorAll('.product__media-list');

  function toggleMobileClasses() {
    const isMobile = window.innerWidth <= 768;
    const classesToToggle = [
      'grid',
      'grid--1-col',
      'grid--1-col-tablet',
      'slider',
      'slider--mobile',
    ];

    mediaLists.forEach(list => {
      classesToToggle.forEach(cls => {
        list.classList.toggle(cls, isMobile);
      });
    });
  }

  toggleMobileClasses();
  window.addEventListener('resize', toggleMobileClasses);
});

// ============================================
// 3. VARIANT CHANGE EVENT HANDLERS
// ============================================

document.addEventListener('DOMContentLoaded', function() {

  document.addEventListener('variant:change', function(evt) {
    const variant = evt.detail.variant;
    
    if (!variant) return;
    
    // Update prices
    const priceContainers = document.querySelectorAll('.product-price-container');
    priceContainers.forEach(container => {
      updatePrice(container, variant);
    });
    // Update add to cart button
    updateAddToCartButton(variant);
  });

});

function updatePrice(priceContainer, variant) {
  if (!priceContainer) return;
  
  const priceElement = priceContainer.querySelector('.price');
  if (!priceElement) return;
  
  const regularPriceElement = priceElement.querySelector('.price-item--regular');
  const salePriceElement = priceElement.querySelector('.price-item--sale');
  const badgeElement = priceElement.querySelector('.badge');
  const badgeElementFinal = document.querySelector('.exluded-final-sale');
  
  const formatMoney = (cents) => {
    return (cents / 100).toFixed(2);
  };
  
  const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
  
  if (onSale) {
    priceElement.classList.add('price--on-sale');
    
    if (salePriceElement) {
      const salePriceBdi = salePriceElement.querySelector('bdi');
      if (salePriceBdi) {
        let prefixSpan = salePriceBdi.querySelector('.price__prefix');
        if (!prefixSpan) {
          prefixSpan = document.createElement('span');
          prefixSpan.className = 'price__prefix';
          prefixSpan.textContent = '$';
        }
        salePriceBdi.innerHTML = '';
        salePriceBdi.appendChild(prefixSpan);
        salePriceBdi.appendChild(document.createTextNode(formatMoney(variant.price)));
      }
    }
    
    if (regularPriceElement) {
      regularPriceElement.style.display = '';
      const regularPriceBdi = regularPriceElement.querySelector('bdi');
      if (regularPriceBdi) {
        let prefixSpan = regularPriceBdi.querySelector('.price__prefix');
        if (!prefixSpan) {
          prefixSpan = document.createElement('span');
          prefixSpan.className = 'price__prefix';
          prefixSpan.textContent = '$';
        }
        regularPriceBdi.innerHTML = '';
        regularPriceBdi.appendChild(prefixSpan);
        regularPriceBdi.appendChild(document.createTextNode(formatMoney(variant.compare_at_price)));
      }
    }
    
    if (badgeElement) {
      badgeElement.style.display = '';
    }

    if (badgeElementFinal) {
      badgeElementFinal.style.display = 'block';
    }

  } else {
    priceElement.classList.remove('price--on-sale');
    
    if (regularPriceElement) {
      const regularPriceBdi = regularPriceElement.querySelector('bdi');
      if (regularPriceBdi) {
        let prefixSpan = regularPriceBdi.querySelector('.price__prefix');
        if (!prefixSpan) {
          prefixSpan = document.createElement('span');
          prefixSpan.className = 'price__prefix';
          prefixSpan.textContent = '$';
        }
        regularPriceBdi.innerHTML = '';
        regularPriceBdi.appendChild(prefixSpan);
        regularPriceBdi.appendChild(document.createTextNode(formatMoney(variant.price)));
      }
    }
    
    /*if (salePriceElement) {
      salePriceElement.style.display = 'none';
    }

    if (badgeElement) {
      badgeElement.style.display = 'none';
    }*/

    if (badgeElementFinal) {
      badgeElementFinal.style.display = 'none';
    }

  }
  
  const unitPriceElement = priceElement.querySelector('.unit-price');
  if (unitPriceElement && variant.unit_price) {
    const unitPriceBdi = unitPriceElement.querySelector('bdi');
    if (unitPriceBdi) {
      unitPriceBdi.textContent = formatMoney(variant.unit_price);
    }
    unitPriceElement.style.display = '';
  } else if (unitPriceElement) {
    unitPriceElement.style.display = 'none';
  }
}

function updateAddToCartButton(variant) {
  const addToCartButtons = document.querySelectorAll('.product-form__submit');
  const strings = window.variantStrings || {};

  addToCartButtons.forEach(button => {
    // CRITICAL: Always remove inline display style
    button.style.removeProperty('display');

    if (variant.available) {
      button.disabled = false;
      button.removeAttribute('disabled');
      button.textContent = button.dataset.preOrder === 'true' ? (strings.preOrder || 'Pre-order') : (strings.addToCart || 'Add to cart');
    } else {
      button.disabled = true;
      button.setAttribute('disabled', 'true');
      button.textContent = strings.soldOut || 'Sold out';
    }
  });
}

// ============================================
// 4. ADD TO CART
// ============================================
// Handled by the theme's ProductForm (assets/global.js): adds without leaving the page and opens
// the cart drawer, which shows the free-shipping progress bar. The old redirect-to-/cart script was
// removed 2026-10-13; it also posted the form a second time alongside ProductForm.

// ============================================
// 5. WISHLIST FUNCTIONALITY
// ============================================
document.addEventListener("DOMContentLoaded", function () {
  var wishlistButtons = document.querySelectorAll('.wisthlist-btn');
  var wishlistRaw = localStorage.getItem('swym-products');

  try {
    var wishlistData = JSON.parse(wishlistRaw);
    var wishlistTitles = wishlistData?.all?.map(item => item.dt?.trim()) || [];

    wishlistButtons.forEach(button => {
      var btnTitle = button.getAttribute('data-title')?.trim();
      if (wishlistTitles.includes(btnTitle)) {
        button.classList.add('active');
      }
    });
  } catch (e) {
    console.error("Invalid wishlist data in localStorage", e);
  }

  document.querySelectorAll('.wisthlist-btn').forEach(btn => {
    btn.addEventListener('click', function () {
      document.getElementById('swym-atw-pdp-button')?.click();

      if (!this.classList.contains('active')) {
        this.classList.add('active');
      }
    });
  });
});

// ---------- Part 2: 2026-10-14 redesign ----------
  // 2026-10-14 redesign — the Add to cart button reads "Add to cart – $185.00" once a size is chosen (price of the
  // chosen size). global.js keeps resetting the label to the plain "Add to cart" (toggleAddButton after each
  // variant change and after the section re-render), so the price is re-appended whenever the label is the plain one.
  (function () {
    try {
      var root = document.querySelector('.product-main-template-v2'); var form = root && document.getElementById(root.dataset.siProductForm || ''); if (!form) return;
      var btn = form.querySelector('[name="add"]'); if (!btn || btn.dataset.priceInLabel !== 'true') return;
      var current = null;
      var money = function (cents) {
        try {
          if (window.theme && theme.Currency && theme.Currency.formatMoney && window.shopSettings && window.shopSettings.moneyFormat) {
            return theme.Currency.formatMoney(cents, window.shopSettings.moneyFormat).replace(/<[^>]*>/g, '').trim();
          }
        } catch (e) {}
        return '$' + (cents / 100).toFixed(2);
      };
      var apply = function () {
        if (!current || !current.available || btn.disabled) return;
        var base = (window.variantStrings && window.variantStrings.addToCart) || 'Add to cart';
        if (btn.textContent.trim() === base.trim()) btn.textContent = base + ' – ' + money(current.price);
      };
      document.addEventListener('variant:change', function (e) { current = e.detail && e.detail.variant; apply(); });
      new MutationObserver(apply).observe(btn, { childList: true, characterData: true, subtree: true });
      // Page opened with a size already chosen (?variant= link, single-size product, gift card): price from the product JSON.
      document.addEventListener('DOMContentLoaded', function () {
        setTimeout(function () {
          try {
            if (current) return;
            var id = form.querySelector('[name="id"]').value;
            var json = document.querySelector('.product-main-template-v2 variant-radios script[type="application/json"], .product-main-template-v2 variant-selects script[type="application/json"]');
            if (!id || !json) return;
            var variants = JSON.parse(json.textContent);
            current = variants.filter(function (v) { return String(v.id) === String(id); })[0] || null;
            apply();
          } catch (e) {}
        }, 400);
      });
    } catch (e) {}
  })();
  // 2026-10-14 redesign — tapping the grey "Select a size" button (pointer-events: none, so the tap lands on the
  // row) scrolls to the size chips and flashes them, like the sticky bar's "Select size" button does.
  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('.product-main-template-v2 product-form:not(.sticky-cart__form) .product-form__buttons').forEach(function (row) {
      row.addEventListener('click', function (e) {
        var btn = row.querySelector('[name="add"]');
        if (!btn || !btn.disabled) return;
        if (e.target.closest && e.target.closest('.shopify-payment-button, #klaviyo-bis-button-container')) return;
        if (btn.textContent.trim() !== (btn.dataset.selectText || 'Select a size')) return;
        var pf = row.closest('product-form');
        if (pf && typeof pf.jumpToVariantPicker === 'function') pf.jumpToVariantPicker();
        var sizes = document.querySelector('.product-main-template-v2 variant-radios [data-size-option]');
        if (sizes) {
          sizes.classList.add('variant-input-wrapper--attention');
          setTimeout(function () { sizes.classList.remove('variant-input-wrapper--attention'); }, 1800);
        }
      });
    });
    // "Don't see your size? Get notified": pick the first sold-out size, which makes Klaviyo put its
    // back-in-stock button in the button slot, then open that form. With nothing sold out in this colour, just
    // scroll to the chips.
    document.querySelectorAll('[data-si-notify]').forEach(function (link) {
      link.addEventListener('click', function () {
        var picker = document.querySelector('.product-main-template-v2 variant-radios'); if (!picker) return;
        var soldOut = picker.querySelector('[data-size-option] input[type="radio"].sold-out');
        var label = soldOut && picker.querySelector('label[for="' + soldOut.id + '"]');
        var pf = document.querySelector('.product-main-template-v2 product-form:not(.sticky-cart__form)');
        if (!label) { if (pf && pf.jumpToVariantPicker) pf.jumpToVariantPicker(); return; }
        if (!soldOut.checked) label.click();
        var tries = 0;
        var timer = setInterval(function () {
          tries += 1;
          var bis = document.querySelector('#klaviyo-bis-button-container button, #klaviyo-bis-button-container a');
          if (bis) { clearInterval(timer); bis.click(); return; }
          if (tries > 25) {
            clearInterval(timer);
            // Klaviyo's form never arrived: go to the block's fallback page if Katie set one, else show the sizes.
            if (link.dataset.siNotifyFallback) { window.location.href = link.dataset.siNotifyFallback; return; }
            if (pf && pf.jumpToVariantPicker) pf.jumpToVariantPicker();
          }
        }, 120);
      });
    });
    // Reviews row link: scroll to the Klaviyo reviews block wherever it sits on the page.
    document.querySelectorAll('[data-si-reviews-link]').forEach(function (link) {
      link.addEventListener('click', function (e) {
        var target = document.querySelector('[id*="klaviyo_reviews_product_reviews"]') || document.querySelector(link.getAttribute('href'));
        if (!target) return;
        e.preventDefault();
        (target.closest('.shopify-section') || target).scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  });
  // 2026-10-14 redesign — desktop: the buy column is pinned while the photos scroll. When the column is taller than
  // the screen (Details open on a long description) it pins by its bottom edge instead, so nothing is cut off.
  (function () {
    try {
      var col = document.querySelector('.product-main-template-v2 .product__info-container--sticky'); if (!col) return;
      var mql = window.matchMedia('(min-width: 750px)');
      var update = function () {
        if (!mql.matches) { col.style.top = ''; return; }
        var top = 32;
        var fit = window.innerHeight - col.offsetHeight - 24;
        col.style.top = Math.min(top, fit) + 'px';
      };
      window.addEventListener('resize', update, { passive: true });
      window.addEventListener('load', update);
      document.addEventListener('toggle', update, true);
      document.addEventListener('variant:change', function () { setTimeout(update, 50); });
      if (window.ResizeObserver) new ResizeObserver(update).observe(col);
      update();
    } catch (e) {}
  })();
  // Repair round 1 — Klaviyo back-in-stock: the moment the app fills #klaviyo-bis-button-container (wherever in
  // the button row it lands) the row gets .si-has-bis and the grey "Sold out" button gives way; the class drops
  // again when the container empties (an in-stock size picked). The CSS sibling rule still covers the usual
  // "container before the button" placement without this script.
  (function () {
    try {
      var rows = document.querySelectorAll('.product-main-template-v2 product-form:not(.sticky-cart__form) .product-form__buttons');
      if (!rows.length) return;
      var sync = function () {
        rows.forEach(function (row) {
          var box = row.querySelector('#klaviyo-bis-button-container');
          row.classList.toggle('si-has-bis', !!(box && box.querySelector('button, a')));
        });
      };
      rows.forEach(function (row) { new MutationObserver(sync).observe(row, { childList: true, subtree: true }); });
      sync();
    } catch (e) {}
  })();
  // Repair round 1 — the lightbox lists only the chosen colour's photos (the same photos the gallery shows), so a
  // shopper on Chocolate Brown does not scroll past beige and black in the zoom.
  (function () {
    try {
      var modal = document.querySelector('.product-media-modal__content'); if (!modal) return;
      var gallery = document.querySelector('.product-main-template-v2 .product__media-list'); if (!gallery) return;
      var sync = function () {
        var visible = {};
        gallery.querySelectorAll('.product__media-item').forEach(function (it) {
          if (getComputedStyle(it).display === 'none') return;
          visible[String(it.dataset.mediaId || '').split('-').pop()] = true;
        });
        if (!Object.keys(visible).length) return;
        modal.querySelectorAll('[data-media-id]').forEach(function (m) {
          if (m.parentElement !== modal) return;
          m.hidden = !visible[String(m.dataset.mediaId).split('-').pop()];
        });
      };
      new MutationObserver(sync).observe(gallery, { attributes: true, subtree: true, attributeFilter: ['class'] });
      window.addEventListener('load', sync);
      sync();
    } catch (e) {}
  })();
  // Sweep 2026-10-14 (M1) — "Don't see your size? Get notified" only shows while a size in the chosen colour is sold
  // out (tapping it with everything in stock did nothing visible). The chips' .sold-out class is per colour and is
  // rewritten on every colour change, so the link follows it; a fit row left with no pill and no link is hidden too.
  (function () {
    var section = document.querySelector('.product-main-template-v2'); if (!section) return;
    if (!section.querySelector('[data-si-notify]')) return;
    var queued = false;
    var sync = function () {
      queued = false;
      var picker = section.querySelector('variant-radios');
      var hasSoldOut = !!(picker && picker.querySelector('[data-size-option] input[type="radio"].sold-out'));
      section.querySelectorAll('[data-si-notify]').forEach(function (link) {
        var holder = link.closest('.si-notify') || link;
        holder.hidden = !hasSoldOut;
        var row = link.closest('.si-fit-row');
        if (row) {
          row.toggleAttribute('data-si-has-sold-out', hasSoldOut);
          row.hidden = !hasSoldOut && !row.querySelector('.si-pill');
        }
      });
    };
    var queue = function () { if (queued) return; queued = true; requestAnimationFrame(sync); };
    new MutationObserver(queue).observe(section, { attributes: true, subtree: true, childList: true, attributeFilter: ['class'] });
    document.addEventListener('variant:change', function () { setTimeout(sync, 50); });
    document.addEventListener('productInfo:loaded', function () { setTimeout(sync, 50); });
    sync();
  })();
  // Sweep 2026-10-14 (H3) — phones: the Zendesk "Help" bubble floats bottom-right, exactly where the size chips,
  // the Delivery toggle and the button row pass as the page scrolls, and it covered "Pick up in store" and the
  // right-hand chips. While any of them is in the bubble's corner, <body> gets si-launcher-clear and
  // assets/custom-style.css fades the bubble out; it is back as soon as they scroll clear. (While the sticky bar
  // is up the same stylesheet already hides it.)
  (function () {
    var targets = document.querySelectorAll('.product-main-template-v2 variant-radios, .product-main-template-v2 .shiping-container, .product-main-template-v2 .si-fit-row, .product-main-template-v2 product-form:not(.sticky-cart__form) .product-form__buttons');
    if (!targets.length) return;
    var mql = window.matchMedia('(max-width: 749px)');
    var ticking = false;
    var update = function () {
      ticking = false;
      var clear = false;
      if (mql.matches) {
        var h = window.innerHeight;
        var launcher = document.querySelector('iframe#launcher');
        var zone = launcher ? launcher.getBoundingClientRect() : null;
        // Zendesk's bubble is 50px tall and sits 72px up on phones (custom-style.css); assume that before it loads.
        var top = zone && zone.height ? zone.top : h - 72 - 50;
        var bottom = zone && zone.height ? zone.bottom : h - 72;
        targets.forEach(function (el) {
          var r = el.getBoundingClientRect();
          if (r.height > 0 && r.bottom > top - 8 && r.top < bottom + 8) clear = true;
        });
      }
      document.body.classList.toggle('si-launcher-clear', clear);
    };
    var onScroll = function () { if (ticking) return; ticking = true; requestAnimationFrame(update); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    document.addEventListener('variant:change', onScroll);
    document.addEventListener('toggle', onScroll, true);
    window.addEventListener('load', onScroll);
    update();
  })();
  // 2026-10-14 — the phone gallery's "1 of 4" bar now counts the slides that are actually on screen (the chosen
  // colour's photos) and follows the swipe; it used to be static markup.
  (function () {
    var slider = document.querySelector('.product-main-template-v2 .product__media-list');
    var fill = document.querySelector('.product-main-template-v2 .slider-progress-fill');
    var current = document.getElementById('slider-current');
    var total = document.getElementById('slider-total');
    if (!slider || !fill || !current || !total) return;
    var update = function () {
      var slides = Array.prototype.filter.call(slider.querySelectorAll('.product__media-item'), function (s) { return s.clientWidth > 0; });
      var n = slides.length; if (!n) return;
      var w = slides[0].clientWidth || 1;
      var i = Math.min(n, Math.max(1, Math.round(slider.scrollLeft / w) + 1));
      total.textContent = n; current.textContent = i; fill.style.width = (i / n * 100) + '%';
    };
    slider.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    new MutationObserver(update).observe(slider, { attributes: true, subtree: true, attributeFilter: ['class'], childList: true });
    update();
  })();
  // 2026-10-14 — delivery estimate ("Ships by … · arrives … – …") from the block settings, in Eastern time, business days only.
  (function () {
    var el = document.querySelector('[data-si-delivery]'); if (!el) return;
    try {
      var cutoff = +el.dataset.cutoff || 12, processing = +el.dataset.processing || 2, tmin = +el.dataset.transitMin || 1, tmax = +el.dataset.transitMax || 5, carrier = el.dataset.carrier || '';
      var parts = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', hour12: false }).formatToParts(new Date());
      var get = function (t) { return +parts.filter(function (p) { return p.type === t; })[0].value; };
      var d = new Date(Date.UTC(get('year'), get('month') - 1, get('day'))); var hour = get('hour') % 24;
      var isBiz = function (x) { var w = x.getUTCDay(); return w !== 0 && w !== 6; };
      var addBiz = function (x, n) { var y = new Date(x.getTime()); while (n > 0) { y.setUTCDate(y.getUTCDate() + 1); if (isBiz(y)) n--; } return y; };
      var start = new Date(d.getTime()); if (!isBiz(start) || hour >= cutoff) { start = addBiz(start, 1); }
      var shipBy = addBiz(start, processing - 1), arriveMin = addBiz(shipBy, tmin), arriveMax = addBiz(shipBy, tmax);
      var fmt = function (x) { return x.toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' }); };
      var cutoffLabel = (cutoff % 12 || 12) + (cutoff < 12 ? ' am' : ' pm');
      var sameDay = isBiz(d) && hour < cutoff;
      el.querySelector('[data-si-delivery-text]').textContent = 'Ships by ' + fmt(shipBy) + ' · arrives ' + fmt(arriveMin) + ' – ' + fmt(arriveMax) + (carrier ? ' (' + carrier + ')' : '') + (sameDay ? '. Order by ' + cutoffLabel + ' ET for same-day processing.' : '.');
      el.removeAttribute('hidden');
    } catch (e) { /* leave hidden */ }
  })();
  // 2026-10-14 — a card opened from a colour-only filter carries ?variant= for the first size in that colour; keep the colour, drop the size.
  document.addEventListener('DOMContentLoaded', function () {
    setTimeout(function () {
      try {
        if (!new URLSearchParams(location.search).get('variant')) return;
        var ref = document.referrer || '';
        if (!/filter\.v\.option\./.test(ref) || /filter\.v\.option\.size/i.test(ref)) return;
        var picker = document.querySelector('variant-radios'); if (!picker) return;
        var size = picker.querySelector('[data-size-option] input[type="radio"]:checked'); if (!size) return;
        size.checked = false;
        picker.dispatchEvent(new Event('change', { bubbles: true }));
      } catch (e) {}
    }, 0);
  });

// ---------- Part 3: "You may also like" rails safety net (2026-10-14 HTML diet) ----------
// sections/product-recommendations.liquid prints blank cards and lets the theme's <product-recommendations>
// element (global.js) fetch the real rails once they are near the viewport. If that fetch fails, or the server
// answers with the blank cards again (it tells the fetched render apart by the capitalised handle in the path),
// the blank boxes must not sit there for good: 12 s after the rails come near the viewport with no real card in
// them, the whole section is removed. Shoppers then see no rail rather than empty boxes.
(function () {
  try {
    var el = document.querySelector('product-recommendations.si-recs--pending');
    if (!el || !('IntersectionObserver' in window)) return;
    var armed = false;
    var check = function () {
      if (!document.body.contains(el)) return;
      if (el.querySelector('.card__media')) { el.classList.remove('si-recs--pending'); return; }
      var section = el.closest('.shopify-section') || el;
      section.parentNode.removeChild(section);
    };
    var io = new IntersectionObserver(function (entries) {
      if (!entries[0].isIntersecting || armed) return;
      armed = true; io.disconnect();
      setTimeout(check, 12000);
    }, { rootMargin: '0px 0px 600px 0px' });
    io.observe(el);
  } catch (e) {}
})();

/* 2026-10-14 (Peter): hovering a colour swatch previews its name in the "Color – …" label; leaving the
   swatch restores the chosen colour. Mouse and keyboard focus only; a tap still selects as before. */
document.addEventListener('DOMContentLoaded', function () {
  document.querySelectorAll('variant-radios fieldset').forEach(function (fieldset) {
    var info = fieldset.querySelector('.form__label-info');
    if (!info || !fieldset.querySelector('label.color__swatch')) return;
    var restore = function () {
      var checked = fieldset.querySelector('input[type=radio]:checked');
      if (checked) info.textContent = '– ' + (checked.dataset.optionValue || checked.value);
    };
    var preview = function (label) {
      var input = label.control || document.getElementById(label.htmlFor);
      if (input) info.textContent = '– ' + (input.dataset.optionValue || input.value);
    };
    fieldset.addEventListener('mouseover', function (e) {
      var label = e.target.closest('label.color__swatch');
      if (label && fieldset.contains(label)) preview(label);
    });
    fieldset.addEventListener('mouseleave', restore);
    fieldset.addEventListener('focusin', function (e) {
      var label = e.target.closest('label.color__swatch, input[type=radio]');
      if (!label) return;
      var l = label.tagName === 'INPUT' ? fieldset.querySelector('label[for="' + label.id + '"].color__swatch') : label;
      if (l) preview(l);
    });
    fieldset.addEventListener('focusout', function () { setTimeout(restore, 0); });
  });
});
